import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCategories } from '../context/CategoriesContext';

import { 
  FaInstagram, 
  FaFacebook, 
  FaEnvelope, 
  FaMapMarkerAlt,
  FaChevronDown,
  FaChevronUp
} from 'react-icons/fa';
import './Footer.css';

const Footer = () => {
  const { categories } = useCategories();
  const navigate = useNavigate();
  const [logoImage, setLogoImage] = useState('');
  const [logoLoading, setLogoLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // State for mobile accordion functionality
  const [expandedSections, setExpandedSections] = useState({
    quickLinks: false,
    collections: false,
    contactInfo: false,
    social: false
  });

  // Toggle accordion section
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Handle collection link click with scroll to top
  const handleCollectionClick = (categoryId) => {
    navigate(`/collections?category=${categoryId}`);
    scrollToTop();
  };

    // Use Firebase Storage logo with responsive sizes
  const fetchLogo = useCallback(async () => {
    try {
      setLogoLoading(true);
      
      // Use Firebase Storage logo with responsive sizes
      const logoUrls = {
        '480w': 'https://images.vastrebyvijh.com/vastre-images/logo/desktop/2025-08-26/logo_desktop_1756240073154_0_480w.webp',
        '768w': 'https://images.vastrebyvijh.com/vastre-images/logo/desktop/2025-08-26/logo_desktop_1756240073154_0_768w.webp',
        '1080w': 'https://images.vastrebyvijh.com/vastre-images/logo/desktop/2025-08-26/logo_desktop_1756240073154_0_1080w.webp',
        '1440w': 'https://images.vastrebyvijh.com/vastre-images/logo/desktop/2025-08-26/logo_desktop_1756240073154_0_1440w.webp',
        '1920w': 'https://images.vastrebyvijh.com/vastre-images/logo/desktop/2025-08-26/logo_desktop_1756240073154_0_1920w.webp'
      };
      
      // Check if Firebase logo exists, otherwise fallback to local webp
      const img = new Image();
      img.onload = () => {
        setLogoImage(logoUrls['1080w']); // Use 1080w as default
        setLogoLoading(false);
      };
      img.onerror = () => {
        // Fallback to local webp logo if Firebase fails
        setLogoImage('/images/logo/hero_desktop_1756135482771_0_1080w.webp');
        setLogoLoading(false);
      };
      img.src = logoUrls['1080w'];
      
    } catch (error) {
      setLogoImage('/images/logo/hero_desktop_1756135482771_0_1080w.webp'); // Use local webp fallback if anything fails
      setLogoLoading(false);
    }
  }, []);

  // Fetch logo when component mounts
  useEffect(() => {
    fetchLogo();
  }, [fetchLogo]);

  return (
    <footer className="footer">
      {/* Top gold border for luxury feel */}
      <div className="footer-top-border"></div>
      
      <div className="footer-content">
        {/* Brand Section - Enhanced with larger logo and better spacing */}
        <div className="footer-brand">
          <div className="footer-logo">
            {logoLoading ? (
              <div className="logo-loading">
                <div className="logo-skeleton"></div>
              </div>
            ) : logoImage ? (
              <img 
                src={logoImage} 
                alt="Vastré By Vijh Logo" 
                className="footer-logo-image"
                loading="lazy"
                                  onError={(e) => {
                    e.target.src = '/images/logo/hero_desktop_1756135482771_0_1080w.webp';
                  }}
              />
            ) : (
              <img 
                src="/images/logo/hero_desktop_1756135482771_0_1080w.webp" 
                alt="Vastré By Vijh Logo" 
                className="footer-logo-image"
                loading="lazy"
              />
            )}
          </div>
          {/* Gold divider line below brand section */}
          <div className="footer-brand-divider"></div>
          <p className="footer-brand-message">
            Crafted With Rarity
          </p>
        </div>

        {/* Quick Links - Enhanced with better spacing and hover effects */}
        <div className="footer-section">
          <div className="footer-section-header" onClick={() => toggleSection('quickLinks')}>
            <h3 className="footer-section-heading">Quick Links</h3>
            <div className="accordion-toggle">
              {expandedSections.quickLinks ? <FaChevronUp /> : <FaChevronDown />}
            </div>
          </div>
          <div className={`footer-section-content ${expandedSections.quickLinks ? 'expanded' : ''}`}>
            <ul className="footer-links-list">
              <li><Link to="/" className="footer-link">Home</Link></li>
              <li><Link to="/about" className="footer-link">About</Link></li>
              <li><Link to="/collections" className="footer-link">Collections</Link></li>
              <li><Link to="/bespoke-consultation" className="footer-link">Bespoke Consultation</Link></li>
              <li><Link to="/contact" className="footer-link">Contact</Link></li>
              <li><Link to="/order-history" className="footer-link">Order History</Link></li>
              <li><Link to="/shipping" className="footer-link">Shipping</Link></li>
              <li><Link to="/cancellation-refunds" className="footer-link">Cancellation & Refunds</Link></li>
            </ul>
          </div>
        </div>

        {/* Collections - Enhanced with better spacing and hover effects */}
        <div className="footer-section">
          <div className="footer-section-header" onClick={() => toggleSection('collections')}>
            <h3 className="footer-section-heading">Collections</h3>
            <div className="accordion-toggle">
              {expandedSections.collections ? <FaChevronUp /> : <FaChevronDown />}
            </div>
          </div>
          <div className={`footer-section-content ${expandedSections.collections ? 'expanded' : ''}`}>
            <ul className="footer-links-list">
              {categories.map((category) => (
                <li key={category.id}>
                  <button 
                    onClick={() => handleCollectionClick(category.id)}
                    className="footer-link"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                  >
                    {category.categoryName || category.id}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Contact Information - Enhanced with icons and better styling (phone number removed) */}
        <div className="footer-section">
          <div className="footer-section-header" onClick={() => toggleSection('contactInfo')}>
            <h3 className="footer-section-heading">Contact Info</h3>
            <div className="accordion-toggle">
              {expandedSections.contactInfo ? <FaChevronUp /> : <FaChevronDown />}
            </div>
          </div>
          <div className={`footer-section-content ${expandedSections.contactInfo ? 'expanded' : ''}`}>
            <div className="footer-contact-info">
              <div className="contact-item">
                <FaEnvelope className="contact-icon" />
                <span>info@vastrebvijh.com</span>
              </div>
              <div className="contact-item">
                <FaMapMarkerAlt className="contact-icon" />
                <span>NEW DELHI 110030</span>
              </div>
            </div>
          </div>
        </div>

        {/* Social Links - Enhanced with gold styling and hover effects */}
        <div className="footer-section">
          <div className="footer-section-header" onClick={() => toggleSection('social')}>
            <h3 className="footer-section-heading">Follow Us</h3>
            <div className="accordion-toggle">
              {expandedSections.social ? <FaChevronUp /> : <FaChevronDown />}
            </div>
          </div>
          <div className={`footer-section-content ${expandedSections.social ? 'expanded' : ''}`}>
            <div className="social-links">
              <a 
                href="https://www.instagram.com/vastrebyvijh/?utm_source=ig_web_button_share_sheet" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-link"
                aria-label="Follow us on Instagram"
              >
                <FaInstagram />
              </a>
              <a 
                href="https://www.facebook.com/profile.php?id=61580272002747" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-link"
                aria-label="Follow us on Facebook"
              >
                <FaFacebook />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Bottom - Enhanced with copyright and better styling */}
      <div className="footer-bottom">
        <div className="footer-bottom-content">
          <p className="copyright-text">
            &copy; 2025 Vastré By Vijh. All rights reserved.
          </p>
          <div className="footer-bottom-links">
            <Link to="/privacy-policy" className="footer-bottom-link">Privacy Policy</Link>
            <Link to="/terms-of-service" className="footer-bottom-link">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 